/**
 * `renderDocToReact` (architecture §11.3, §11.5): the read-only render of a stored body as React elements — never
 * `innerHTML`, never lowlight for a code block without a language. Opening an issue paints this first; TipTap mounts
 * on focus or idle. Mention labels come from the store at display time (`labels`, bound to the viewer); invisible
 * targets render "Private issue"; inert user chips render the name without a link. Links are re-validated at render.
 */
import { createElement, type ReactElement, type ReactNode } from 'react';
import type { DocJSON, MarkJSON, Reference } from './doc';
import { safeHref } from './links';
import { resolveMention, type LabelResolver } from './text';

export interface RenderOptions {
  labels: LabelResolver;
  publicUrl: string;
  /** In-app path of a mentioned entity the viewer can see (`/acme/issue/ENG-12`). */
  linkFor?: (kind: Reference['kind'], id: string) => string | undefined;
  /** URL of an uploaded image the viewer can read. */
  fileUrl?: (fileId: string) => string | undefined;
  /** Attributes for every mention chip (the host's `data-testid`). */
  chipAttrs?: Readonly<Record<string, string>>;
  /** A glyph before a visible chip's label (an issue chip's status icon); null or undefined for none. */
  chipIcon?: (kind: Reference['kind'], id: string) => ReactNode;
}

function marked(node: ReactNode, marks: readonly MarkJSON[] | undefined, o: RenderOptions, key: string): ReactNode {
  let out = node;
  for (const m of marks ?? []) {
    switch (m.type) {
      case 'bold':
        out = createElement('strong', { key }, out);
        break;
      case 'italic':
        out = createElement('em', { key }, out);
        break;
      case 'strike':
        out = createElement('s', { key }, out);
        break;
      case 'code':
        out = createElement('code', { key }, out);
        break;
      case 'link': {
        const href = safeHref(m.attrs?.href, o.publicUrl);
        if (href) out = createElement('a', { key, href, rel: 'noopener noreferrer nofollow', target: '_blank' }, out);
        break;
      }
      default:
        break;
    }
  }
  return out;
}

function render(n: DocJSON, o: RenderOptions, key: string): ReactNode {
  const kids = (): ReactNode[] => (n.content ?? []).map((c, i) => render(c, o, `${key}.${i}`));
  switch (n.type) {
    case 'doc':
      return createElement('div', { key, className: 'qd-doc' }, ...kids());
    case 'paragraph':
      return createElement('p', { key }, ...kids());
    case 'heading': {
      const level = Math.min(3, Math.max(1, Number(n.attrs?.level ?? 1)));
      return createElement(`h${level}`, { key }, ...kids());
    }
    case 'blockquote':
      return createElement('blockquote', { key }, ...kids());
    case 'bulletList':
      return createElement('ul', { key }, ...kids());
    case 'taskList':
      return createElement('ul', { key, 'data-type': 'taskList' }, ...kids());
    case 'orderedList': {
      const start = Number(n.attrs?.start ?? 1);
      return createElement('ol', { key, ...(start !== 1 && Number.isFinite(start) && { start }) }, ...kids());
    }
    case 'listItem':
      return createElement('li', { key }, ...kids());
    case 'taskItem':
      return createElement(
        'li',
        { key, 'data-checked': n.attrs?.checked === true ? 'true' : 'false' },
        createElement('input', { type: 'checkbox', checked: n.attrs?.checked === true, disabled: true, readOnly: true }),
        ...kids(),
      );
    case 'codeBlock':
      return createElement('pre', { key }, createElement('code', null, (n.content ?? []).map((c) => c.text ?? '').join('')));
    case 'horizontalRule':
      return createElement('hr', { key });
    case 'hardBreak':
      return createElement('br', { key });
    case 'text':
      return marked(n.text ?? '', n.marks, o, key);
    case 'mention': {
      const kind = n.attrs?.kind as Reference['kind'];
      const id = String(n.attrs?.id);
      const inert = n.attrs?.inert === true;
      const { label, visible } = resolveMention(kind, id, o.labels);
      const href = visible && !inert ? o.linkFor?.(kind, id) : undefined;
      const safe = href === undefined ? null : safeHref(href, o.publicUrl);
      const props = {
        ...o.chipAttrs,
        key,
        className: 'qd-mention',
        'data-mention-kind': kind,
        'data-kind': kind,
        'data-id': id,
        ...(inert && { 'data-inert': 'true' }),
        ...(!visible && { 'data-private': 'true' }),
      };
      const icon = visible ? (o.chipIcon?.(kind, id) ?? null) : null;
      const inner = icon === null ? [label] : [createElement('span', { key: 'i', className: 'qd-mention-icon', 'aria-hidden': true }, icon), label];
      const chip = safe ? createElement('a', { ...props, href: safe }, ...inner) : createElement('span', props, ...inner);
      return marked(chip, n.marks, o, `${key}m`);
    }
    case 'image': {
      const src = o.fileUrl?.(String(n.attrs?.fileId));
      const safe = src === undefined ? null : safeHref(src, o.publicUrl);
      return safe ? createElement('img', { key, src: safe, alt: '', loading: 'lazy', decoding: 'async' }) : createElement('span', { key, className: 'qd-image-missing' }, '[image]');
    }
    default:
      return null;
  }
}

/** React elements of a stored (validated) document. */
export function renderDocToReact(doc: DocJSON, o: RenderOptions): ReactElement {
  return render(doc, o, 'd') as ReactElement;
}
